import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  RIDER_TYPES,
  RIDER_TYPE_LABELS,
  isRiderType,
  requestBondRider,
  chosenBond,
  issuedBondOptions,
  type IssuedBondChoice,
  type RiderType,
} from "@/lib/bondServicing";
import {
  bondEditIssues,
  changedBondValues,
  editableBag,
  editableFinalFields,
} from "@/lib/bondEdit";
import { toRenderFinalField, type BondValueBag } from "@/lib/bondFields";
import {
  bondFieldMarkers,
  readBondActions,
  readEditPolicy,
} from "@/lib/bondStateModel";
import {
  BOND_FINAL_STEP_KEY,
  type BondFinalField,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { ActionFormDialog } from "@/components/ActionFormDialog";
import { BondDetailsFields } from "@/components/BondDetailsFields";
import { SelectField } from "@/components/SelectField";
import { TextAreaField } from "@/components/TextAreaField";
import {
  IssuedBondField,
  IssuedBondsNotice,
} from "@/components/bond-submission/IssuedBondField";

// BRK-S412 — "Request a rider": change an issued bond's amount, names or dates
// (PMS-S850). Posts `{bondId, type, details}` to the bond's
// submission; "Rider request sent to Signal" only from the 2xx, the
// server's refusal inline with the inputs kept.
//
// SHARED: the Surety account's Actions menu opens it over every issued bond;
// the submission page's action row (BRK-S420) passes its own `submissionId`
// and, when it knows one, the `bondId` to start on.
//
// ON THE SUBMISSION PAGE (BRK-S420) the page also passes its `order` and the
// configuration's final-step `fields`, and the dialog draws the bond details
// the rider changes through the ONE shared renderer (`BondDetailsFields`,
// BRK-S418) exactly as the wizard does: prefilled from `values.final`, PMS's
// `editPolicy.locks` read-only with their reason, and the served markers. The
// create then carries `values` — ONLY the changed fields, in the contract's
// spelling (`changedBondValues`, D-10) — after the wizard's own checks
// (`bondEditIssues`). Nothing about the rider is decided here: the page opens
// it only while `actions.rider` is granted, and the 201 — the ORDER VIEW
// (PMS-S850, BRK-S511) — goes to `onRequested`, so the page shows the
// server's answer (the banner, the markers, Request Rider greyed).
//
// FROM THE ACCOUNT (BRK-S495) the Actions menu has no order of its own: the
// dialog says which bond is chosen (`onBondChange`), the menu reads that
// bond's submission and configuration and passes them in as `order` and
// `fields`, and the same details draw and the same `values` go out. Switching
// bond re-reads and resets them. While they load the dialog says so; a failed
// order read (`detailsUnavailable`) keeps the request takeable in Details
// alone. A submission whose `actions.rider` is not granted shows PMS's reason
// and offers no send — the server's verdict, not a client rule.

const FIELDS = ["bondId", "type", "details"] as const;
const DETAILS_MAX = 2000;
const NO_FIELDS: readonly BondFinalField[] = [];

export function RequestRiderDialog({
  open,
  onClose,
  bonds,
  bondsError = null,
  submissionId,
  bondId,
  order,
  fields,
  onRequested,
  onBondChange,
  detailsLoading = false,
  detailsUnavailable = false,
}: {
  open: boolean;
  onClose: () => void;
  /** The issued bonds to offer; null while they are read. */
  bonds: readonly IssuedBondChoice[] | null;
  bondsError?: string | null;
  /** Narrow the bonds to one submission (the submission page). */
  submissionId?: string;
  /** The bond to start on. */
  bondId?: string;
  /** The submission page's order: draw its bond details (BRK-S420). */
  order?: BondSubmission;
  /** With `order`: the configuration's final-step fields; null when the
   *  read failed (the request is then taken in Details alone). */
  fields?: readonly BondFinalField[] | null;
  /** After the 2xx only: the order view the 201 answered with. */
  onRequested?: (order: BondSubmission) => void;
  /** The account side: the chosen bond's submission, as it changes. */
  onBondChange?: (submissionId: string | null) => void;
  /** The account side: the chosen bond's order and configuration are being
   *  read. */
  detailsLoading?: boolean;
  /** The account side: the chosen bond's order could not be read. */
  detailsUnavailable?: boolean;
}) {
  const { authedRequest } = useAuth();
  // The order as it read when the dialog opened (the page mounts it while
  // open): the page's re-read after the 2xx must not reset the form under
  // its own confirmation.
  const [opened, setOpened] = useState(order);
  // A different order — the account side's next bond — replaces it; the
  // page's re-read of the same order does not.
  useEffect(() => {
    if (order?.id !== opened?.id) setOpened(order);
  }, [order, opened]);
  // The bond details, drawn only on the submission page and only when the
  // configuration was read.
  const declared = useMemo(
    () => (opened && fields ? editableFinalFields(fields) : NO_FIELDS),
    [opened, fields],
  );
  const rendered = useMemo(() => declared.map(toRenderFinalField), [declared]);
  const held = useMemo<BondValueBag>(
    () => editableBag(opened?.values?.[BOND_FINAL_STEP_KEY] ?? {}),
    [opened],
  );
  const locks = useMemo(
    () => readEditPolicy(opened?.editPolicy)?.locks ?? {},
    [opened],
  );
  const { markers, onBond } = useMemo(
    () => bondFieldMarkers(opened ?? {}),
    [opened],
  );
  const [values, setValues] = useState<BondValueBag>(held);
  const { options, initial } = useMemo(
    () => issuedBondOptions(bonds ?? [], submissionId, bondId),
    [bonds, submissionId, bondId],
  );
  const [bondKey, setBondKey] = useState(initial);
  const [type, setType] = useState<RiderType | "">("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBondKey(initial);
    setType("");
    setDetails("");
    setError(null);
    setInvalid({});
    setSuccess(null);
  }, [open, initial]);

  // The details follow the order they were drawn from (BRK-S495).
  useEffect(() => {
    setValues(held);
    setInvalid({});
  }, [held]);

  const bondChanged = useRef(onBondChange);
  bondChanged.current = onBondChange;
  const chosenSubmission = chosenBond(options, bondKey)?.submissionId ?? null;
  useEffect(() => {
    if (open) bondChanged.current?.(chosenSubmission);
  }, [open, chosenSubmission]);

  const rider = opened ? readBondActions(opened.actions)?.rider : undefined;
  const refused = rider !== undefined && !rider.enabled;

  function onFieldChange(name: string, value: BondValueBag[string]) {
    setValues((all) => ({ ...all, [name]: value }));
    setInvalid((all) => {
      const rest = { ...all };
      delete rest[name];
      return rest;
    });
  }

  async function send() {
    const bond = chosenBond(options, bondKey);
    const text = details.trim();
    const missing: Record<string, string> = {
      ...bondEditIssues(rendered, values, locks),
    };
    if (!bond) missing.bondId = "Choose a bond.";
    if (type === "") missing.type = "Choose a rider type.";
    if (text === "") missing.details = "Describe the change.";
    setInvalid(missing);
    setError(null);
    if (!bond || type === "" || Object.keys(missing).length > 0) return;
    if (refused) return;
    setBusy(true);
    try {
      const fresh = await requestBondRider(authedRequest, bond.submissionId, {
        bondId: bond.bondId,
        type,
        details: text,
        ...(opened
          ? { values: changedBondValues(rendered, held, values, locks) }
          : {}),
      });
      setSuccess("Rider request sent to Signal");
      onRequested?.(fresh);
    } catch (err) {
      const anchored = anchorServerError(err, [
        ...FIELDS,
        ...rendered.map((f) => f.name),
      ]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  const ready = bonds !== null && options.length > 0;
  const drawn = opened !== undefined && !refused;
  return (
    <ActionFormDialog
      open={open}
      onClose={onClose}
      // Wide enough for the wizard's two-column grid when it is drawn.
      maxWidth={opened ? "md" : "sm"}
      title="Request a rider"
      lead="Request a rider to change the terms of an issued bond. Signal reviews it before anything changes."
      submitLabel="Request rider"
      busy={busy}
      error={error}
      success={success}
      canSubmit={ready && !refused && !detailsLoading}
      onSubmit={() => void send()}
    >
      <IssuedBondsNotice
        bonds={bonds === null ? null : options}
        bondsError={bondsError}
        emptyText={`There are no issued bonds on this ${
          submissionId === undefined ? "account" : "submission"
        } yet, so there’s nothing to change.`}
      />
      {ready && (
        <>
          <IssuedBondField
            options={options}
            value={bondKey}
            onChange={setBondKey}
            error={invalid.bondId}
          />
          <SelectField
            label="Rider type"
            required
            placeholder="Select a rider type"
            value={type}
            options={RIDER_TYPES.map((t) => ({
              value: t,
              label: RIDER_TYPE_LABELS[t],
            }))}
            onChange={(e) => {
              const next = e.target.value;
              setType(isRiderType(next) ? next : "");
            }}
            error={invalid.type}
          />
          {detailsLoading && (
            <p role="status" className="text-sm text-brk-dim">
              Loading this bond’s details…
            </p>
          )}
          {refused && (
            <p role="note" className="text-sm text-brk-text">
              {rider.reason ??
                "A rider can’t be requested on this bond right now."}
            </p>
          )}
          {!detailsLoading && detailsUnavailable && (
            <p role="note" className="text-sm text-brk-dim">
              We couldn’t load this bond’s details, so describe the change in
              Details below.
            </p>
          )}
          {drawn &&
            !detailsLoading &&
            (fields ? (
              <section
                aria-label="What the rider changes"
                className="flex min-w-0 flex-col gap-3"
              >
                <p className="text-sm text-brk-dim">
                  Change the bond details this rider amends. Details that can’t
                  change now say why.
                </p>
                <BondDetailsFields
                  fields={declared}
                  values={values}
                  onChange={onFieldChange}
                  invalid={invalid}
                  locks={locks}
                  markers={markers}
                  onBond={editableBag(onBond)}
                />
              </section>
            ) : (
              <p role="note" className="text-sm text-brk-dim">
                We couldn’t load this bond’s details, so describe the change in
                Details below.
              </p>
            ))}
          <TextAreaField
            label="Details"
            required
            maxLength={DETAILS_MAX}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            error={invalid.details}
          />
        </>
      )}
    </ActionFormDialog>
  );
}
